import React, { useState, useEffect } from "react";
import {
  Phone, Backspace, MicrophoneSlash, Microphone,
  PauseCircle, PlayCircle, PhoneDisconnect, PhoneCall,
  ArrowDown, ArrowUp, ArrowsLeftRight
} from "@phosphor-icons/react";
import API from "../services/api";
import "./Phone.css";

const DIALPAD = [
  ["1",""],["2","ABC"],["3","DEF"],
  ["4","GHI"],["5","JKL"],["6","MNO"],
  ["7","PQRS"],["8","TUV"],["9","WXYZ"],
  ["*",""],["0","+"],["#",""],
];

export default function PhonePage() {
  const dialerState = window.__ds || {};
  const [number, setNumber] = useState("");
  const [callerId, setCallerId] = useState("");
  const [numbers, setNumbers] = useState([]);
  const [recentCalls, setRecentCalls] = useState([]);
  const [tab, setTab] = useState("dialpad");
  const [showTransfer, setShowTransfer] = useState(false);
  const [agents, setAgents] = useState([]);
  const [transferring, setTransferring] = useState(false);

  const {
    status, isReady, isInCall, isMuted, isOnHold,
    formattedDuration, error, dial, hangup,
    toggleMute, toggleHold, sendDTMF,
  } = dialerState;

  useEffect(() => {
    API.get("/twilio/credentials").then(({ data }) => {
      const nums = [];
      if (data.caller_id) nums.push({ label: `Regular · ${data.caller_id}`, value: data.caller_id });
      if (data.tollfree_number) nums.push({ label: `Toll-Free · ${data.tollfree_number}`, value: data.tollfree_number });
      setNumbers(nums);
      if (nums.length) setCallerId(nums[0].value);
    }).catch(() => {});
    API.get("/calls?limit=20").then(({ data }) => setRecentCalls(data)).catch(() => {});
    API.get("/auth/teammates").then(({ data }) => {
      setAgents((data || []).filter(a => a.agent_identity));
    }).catch(() => {});
  }, []);

  const handleTransfer = async (targetAgentIdentity) => {
    if (!dialerState.activeCall) return;
    const callSid = dialerState.activeCall.parameters?.CallSid;
    if (!callSid) return;
    setTransferring(true);
    try {
      await API.post("/twilio/transfer-to-agent", {
        call_sid: callSid,
        target_agent_identity: targetAgentIdentity,
      });
      setShowTransfer(false);
      dialerState.hangup();
    } catch (err) {
      alert("Transfer failed: " + (err.response?.data?.error || err.message));
    }
    setTransferring(false);
  };

  const handleKey = (digit) => {
    if (isInCall) { sendDTMF(digit); return; }
    setNumber(n => n + digit);
  };

  const statusColor = { idle: isReady ? "var(--color-teal)" : "var(--color-orange)", connecting: "var(--color-orange)", ringing: "var(--color-blue)", "in-call": "var(--color-teal)", error: "var(--color-danger)" }[status] || "var(--color-dark-gray)";
  const statusLabel = { idle: isReady ? "Ready" : "Connecting...", connecting: "Dialing...", ringing: "Ringing...", "in-call": formattedDuration, error: "Error" }[status] || "—";
  const formatDate = (d) => { const date = new Date(d), now = new Date(), diff = now - date; return diff < 86400000 ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : date.toLocaleDateString([], { month: "short", day: "numeric" }); };
  const getDisplayNumber = (call) => call.direction === "inbound" ? call.from_number : call.to_number;

  return (
    <div className="phone-page">
      <div className="dialer-panel">
        <div className="dialer-header">
          <div className="dialer-status">
            <span className="status-dot-sm" style={{ background: statusColor }} />
            <span style={{ color: statusColor, fontSize: 12, fontWeight: 500 }}>{statusLabel}</span>
          </div>
          {numbers.length > 1 && (
            <select value={callerId} onChange={e => setCallerId(e.target.value)} className="caller-id-select" disabled={isInCall}>
              {numbers.map(n => <option key={n.value} value={n.value}>{n.label}</option>)}
            </select>
          )}
        </div>
        {error && <div className="msg-error" style={{ margin: "0 16px 8px" }}>{error}</div>}
        <div className="number-display">
          <input className="number-input" value={number} onChange={e => setNumber(e.target.value)} placeholder="+1 (555) 000-0000" disabled={isInCall} autoFocus />
          {number && !isInCall && (
            <button className="btn-icon backspace" onClick={() => setNumber(n => n.slice(0, -1))}>
              <Backspace size={18} />
            </button>
          )}
        </div>
        <div className="dialer-tabs">
          <button className={`dialer-tab ${tab === "dialpad" ? "active" : ""}`} onClick={() => setTab("dialpad")}>Dialpad</button>
          <button className={`dialer-tab ${tab === "recents" ? "active" : ""}`} onClick={() => setTab("recents")}>Recents</button>
        </div>
        {tab === "dialpad" && (
          <div className="dialpad-grid">
            {DIALPAD.map(([digit, letters]) => (
              <button key={digit} className="dialpad-key" onClick={() => handleKey(digit)}>
                <span className="key-digit">{digit}</span>
                {letters && <span className="key-sub">{letters}</span>}
              </button>
            ))}
          </div>
        )}
        {tab === "recents" && (
          <div className="recents-list">
            {recentCalls.length === 0 && <div className="empty-state"><p>No recent calls</p></div>}
            {recentCalls.map(call => (
              <div key={call.id} className="recent-item" onClick={() => !isInCall && setNumber(getDisplayNumber(call))}>
                <div className={`recent-direction ${call.direction}`}>
                  {call.direction === "inbound" ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
                </div>
                <div className="recent-info">
                  <div className="recent-name">{call.first_name ? `${call.first_name} ${call.last_name}` : getDisplayNumber(call)}</div>
                  <div className="recent-meta">{call.direction} · {call.status} · {formatDate(call.created_at)}</div>
                </div>
                <button className="btn-icon" onClick={e => { e.stopPropagation(); if (!isInCall) dial(getDisplayNumber(call), callerId); }}>
                  <PhoneCall size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="call-controls">
          {!isInCall ? (
            <button className="btn-call" onClick={() => dial(number.trim(), callerId)} disabled={!isReady || !number.trim()}>
              <Phone size={18} weight="fill" /> Call
            </button>
          ) : (
            <div className="in-call-controls">
              <button className={`ctrl-btn ${isMuted ? "active" : ""}`} onClick={toggleMute}>
                {isMuted ? <MicrophoneSlash size={20} /> : <Microphone size={20} />}
                <span>{isMuted ? "Unmute" : "Mute"}</span>
              </button>
              <button className="ctrl-btn hangup" onClick={hangup}>
                <PhoneDisconnect size={20} />
                <span>End</span>
              </button>
              {agents.length > 0 && (
                <button className={`ctrl-btn ${showTransfer ? "active" : ""}`} onClick={() => setShowTransfer(s => !s)}>
                  <ArrowsLeftRight size={20} />
                  <span>Transfer</span>
                </button>
              )}
              <button className={`ctrl-btn ${isOnHold ? "active" : ""}`} onClick={toggleHold}>
                {isOnHold ? <PlayCircle size={20} /> : <PauseCircle size={20} />}
                <span>{isOnHold ? "Resume" : "Hold"}</span>
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="call-info-panel">
        {isInCall ? (
          <div className="active-call-card">
            <div className="active-call-avatar">{(number || "?")[0].toUpperCase()}</div>
            <div className="active-call-number">{number}</div>
            <div className="active-call-status" style={{ color: statusColor }}>{statusLabel}</div>
            {showTransfer && (
              <div style={{marginTop:16,width:"100%"}}>
                <div style={{fontSize:11,color:"var(--color-gray)",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Transfer to</div>
                {agents.map(agent => (
                  <button key={agent.id} onClick={() => handleTransfer(agent.agent_identity)}
                    disabled={transferring}
                    style={{width:"100%",padding:"10px 16px",marginBottom:6,background:"var(--color-surface)",border:"1px solid var(--color-surface-mid)",borderRadius:8,color:"var(--color-white)",cursor:"pointer",display:"flex",alignItems:"center",gap:10,fontSize:13}}>
                    <div style={{width:28,height:28,borderRadius:"50%",background:"var(--color-teal)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,flexShrink:0}}>
                      {agent.first_name[0]}{agent.last_name[0]}
                    </div>
                    <div style={{textAlign:"left"}}>
                      <div style={{fontWeight:600}}>{agent.first_name} {agent.last_name}</div>
                      <div style={{fontSize:11,color:"var(--color-gray)"}}>{agent.agent_identity}</div>
                    </div>
                    {transferring && <span style={{marginLeft:"auto",fontSize:11,color:"var(--color-gray)"}}>...</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="idle-state">
            <Phone size={48} weight="thin" style={{ opacity: 0.2 }} />
            <h3>Leovoid UCaaS</h3>
            <p>Enter a number or select a contact to start a call.</p>
            <div className="idle-status">
              <span className="status-dot-sm" style={{ background: statusColor }} />
              <span style={{ color: statusColor }}>{statusLabel}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
